import {
    getOrderEmailRecipients,
    updateOrderEmailRecipients,
    type OrderEmailDocument,
    type OrderEmailRecipients,
} from '@api/organization';
import { isSupplierVendor, listVendors } from '@api/vendors';
import { listVendorsForPicker } from '@api/work-order';
import {
    Button,
    Divider,
    Group,
    Loader,
    Paper,
    Stack,
    TagsInput,
    Text,
    Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FC } from 'react';

import VendorRecipientOverrides from './VendorRecipientOverrides';

/**
 * Who is copied when an order document goes out, configured per counterparty.
 *
 * ONE COMPONENT FOR BOTH ORDER EMAILS. A production order and a purchase order ask the same
 * question of the same recipient book and differ in exactly one way: which vendors can receive
 * one. Two near-identical screens would have been two places to fix every future change.
 *
 * THE VENDOR LISTS ARE DELIBERATELY DIFFERENT, and the caption says so or it reads as a bug. A
 * production order goes to a manufacturer; a purchase order goes to a supplier. For Troovy
 * that is 13 vendors and a different 15 — overlapping, not equal.
 *
 * THESE SEED THE SEND DIALOG, they do not decide the send. Both documents are emailed by a
 * person who reviews the recipients first — the production order's dialog exists precisely to
 * make them name the recipient — so a configured address the sender deletes stays deleted.
 */
const OrderEmailSection: FC<{ document: OrderEmailDocument; editable: boolean }> = ({
    document,
    editable,
}) => {
    /*
     * Both documents keep a saved To. The purchase order briefly did not: while sending
     * rewrote the order's own `vendor_email`, a saved address was a rival for a field the
     * sender could already see on the order. Sending no longer touches the order, so the two
     * are separate facts — what the document records about the counterparty, and where the
     * mail actually goes.
     *
     * What still differs is which vendors can receive one, which is why this prop survives.
     */
    const isProduction = document === 'production-order';

    const config = useQuery({
        queryKey: ['order-email-recipients', document],
        queryFn: () => getOrderEmailRecipients(document),
    });

    const vendors = useQuery({
        queryKey: ['order-email-vendors', document],
        queryFn: async () =>
            isProduction
                ? listVendorsForPicker()
                : (await listVendors())
                      .filter(isSupplierVendor)
                      .map((v) => ({ vendorId: v.vendorId, name: v.name })),
    });

    const [alwaysCc, setAlwaysCc] = useState<string[]>([]);
    const [byVendor, setByVendor] = useState<NonNullable<OrderEmailRecipients['byVendor']>>({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!config.data) return;
        setAlwaysCc(config.data.alwaysCc ?? []);
        setByVendor(config.data.byVendor ?? {});
    }, [config.data]);

    const touch = () => {
        setSaved(false);
        setError(null);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const next = await updateOrderEmailRecipients(document, { alwaysCc, byVendor });
            // Re-seeded from what came back, not from the draft: the server drops vendors
            // nobody is copied for, and a screen still showing them would re-send them.
            setAlwaysCc(next.alwaysCc ?? []);
            setByVendor(next.byVendor ?? {});
            setSaved(true);
        } catch (err: any) {
            setError(
                err?.error?.data?.message
                    ?? err?.error?.message
                    ?? err?.message
                    ?? 'Could not save. Try again.',
            );
        } finally {
            setSaving(false);
        }
    };

    if (config.isLoading || vendors.isLoading) return <Loader size="sm" />;

    const noun = isProduction ? 'production order' : 'purchase order';
    const party = isProduction ? 'manufacturer' : 'supplier';

    return (
        <Paper p="md" radius="md" withBorder>
            <Stack gap="sm">
                <div>
                    <Title order={5}>
                        {isProduction ? 'Production order' : 'Purchase order'}
                    </Title>
                    <Text size="sm" c="dimmed">
                        {`Opens the send dialog with these people already filled in. Whoever sends
                          the ${noun} can still change them before it goes.`}
                    </Text>
                </div>

                <TagsInput
                    label="Always CC"
                    description={`Copied on every ${noun}, whoever it is going to.`}
                    placeholder="name@company.com"
                    value={alwaysCc}
                    disabled={!editable}
                    onChange={(v) => {
                        touch();
                        setAlwaysCc(v);
                    }}
                />

                <Divider />

                <VendorRecipientOverrides
                    vendors={vendors.data}
                    byVendor={byVendor}
                    onChange={(next) => {
                        touch();
                        setByVendor(next);
                    }}
                    editable={editable}
                    party={party}
                    hint={
                        isProduction
                            ? 'Manufacturers, because a production order is handed to someone who makes.'
                            : 'Suppliers, because a purchase order is placed with someone who sells. A different list from the Production order tab by design.'
                    }
                    toDescription={
                        isProduction
                            ? 'Leave blank to use the address on the supplier record.'
                            : 'Leave blank to use the contact recorded on each order. Sending does not change that contact unless the sender asks it to.'
                    }
                    ccDescription={`Copied on this ${party}'s orders only.`}
                />

                {error && (
                    <Text size="xs" c="red.7">
                        {error}
                    </Text>
                )}

                {editable ? (
                    <Group justify="flex-end">
                        <Button size="xs" loading={saving} onClick={() => void save()}>
                            {saved ? 'Saved' : 'Save'}
                        </Button>
                    </Group>
                ) : (
                    <Text size="xs" c="dimmed">
                        Only admins can change who receives these emails.
                    </Text>
                )}
            </Stack>
        </Paper>
    );
};

export default OrderEmailSection;
